import { useEffect, useState, useMemo } from "react";
import {
    Activity, HeartPulse,
    Wifi, WifiOff, ChevronLeft, ChevronRight,
    Cctv, AlertCircle, X, History, TrendingUp, LayoutDashboard, Building2,
    Cpu, Database, HardDrive,
    Server, Bell, BellOff, BellRing, RefreshCw
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Label,
    XAxis, YAxis, CartesianGrid,
    AreaChart, Area,
} from "recharts";
import baseapi from "../../services/baseapi";
import { useClientLevel } from "../../hooks/useClientLevel";
import { showError } from "../../utils/toast";
import SingleSelectLocalSearchDropdown from "../../components/SingleSelectLocalSearchDropdown";
import "leaflet/dist/leaflet.css";
import { Map as MapIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
// import LocationSelectorSection, { type LocationIds } from "../../components/LocationSelectorSection";
import { type LocationIds } from "../../components/LocationSelectorSection";
// import CameraEventsPopover from "../../components/CameraEventsPopover";
import { CameraListSidebar } from "./CameraListSidebar";
import { SiteListSidebar } from "./SiteListSidebar";
import { NvrListSidebar } from "./NvrListSidebar";
import { EventListSidebar } from "./EventListSidebar";
import { MiniMap } from "./MiniMap";

// --- Interfaces ---
interface DashboardData {
    overview: any;
    uptime: { stats: any; cameras: any[] };
    flapping: any[];
    reliability_dashboard?: { stats: any; cameras: any[] };
    manufacturer_health?: any[];
    health_timeseries: any[];
    trend_analysis?: {
        daily_events_30d: any[];
        camera_downtime_trend?: any[];
        clients_uptime_trend?: any[];
    };
    daily_events_30d?: any[]; // Fallback for some global level versions
    clients_uptime_trend?: any[]; // Fallback for some global level versions
    camera_downtime_trend?: any[]; // Fallback
    fleet?: any[]; // For asset lists
    alerts: { stats: any; open_alerts: any[]; closed_30d: any[] };
    clients?: { total: number; total_online: number; total_offline: number };
}

interface NvrDashboardData {
    device_overview: {
        total_nvrs: number;
        total_channels: number;
        unique_models: string[];
        unique_firmware: string[];
    };
    storage: {
        total_disks: number;
        total_capacity_tb: number;
        total_used_tb?: number;
        total_free_tb?: number;
        utilization_percent?: number;
        nvrs_full?: number;

        overwrite_enabled: number;
        overwrite_disabled: number;
        min_retention_set: number;
        min_retention_none: number;
        disk_errors: number;
        disk_health_ok: number;
        disk_health_not_ok: number;
        failure_alert_enabled: number;
        failure_alert_disabled: number;
        email_on_failure_on: number;
        email_on_failure_off: number;
        missing_disk_alert_enabled: number;
        missing_disk_alert_disabled: number;
        low_space_alert_enabled?: number;
        low_space_alert_disabled?: number;
    };
    recording: {
        total_channels_recording: number;
        total_channels_not_recording: number;
        recording_rate_percent: number;
    };
    detection: {
        motion_detection_enabled: number;
        tamper_detection_enabled: number;
        alarm_inputs_active: number;
    };
    network: {
        ntp_enabled_count: number;
        ntp_disabled_count: number;
        dhcp_enabled_interfaces: number;
        static_ip_interfaces: number;
    };
    health: {
        total_critical: number;
        total_warnings: number;
        total_ok: number;
        critical_issues: any[];
        warnings: any[];
        ok_items: any[];
    };
    fleet_stats?: {
        total_nvrs: number;
        recording_stopped: number;
        overwrite_disabled_and_full: number;
        ntp_off: number;
        brute_force_off: number;
        no_email_alerts: number;
        healthy_nvrs: number;
        fleet_health_score: number;
    };
    fleet_health_breakdown?: {
        [key: string]: {
            count: number;
            percent: number;
            severity: string;
        };
    };
    nvr_list?: any[];
}

// --- Components ---

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white border border-slate-200 p-2 rounded-lg shadow-xl z-50">
                <p className="text-slate-900 text-[9px] font-black mb-1 uppercase tracking-wider">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-[10px] mb-0.5">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry?.color || entry?.payload?.fill }} />
                        <span className="text-slate-500 font-bold uppercase">{entry?.name || entry?.dataKey}:</span>
                        <span className="text-slate-900 font-black">{entry?.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};


const StatCard = ({ icon: Icon, label, value, color, bg, secondaryStats, onClick }: any) => (
    <div
        onClick={onClick}
        className={`bg-white border border-slate-200 rounded-lg p-2 flex items-center gap-2 shadow-sm transition-all group overflow-hidden relative ${onClick ? 'cursor-pointer hover:border-[#422568]/45 hover:bg-slate-50/30' : 'hover:border-[#422568]/20'
            }`}
    >
        <div className={`p-1.5 rounded-lg ${bg} ${color} transition-transform group-hover:scale-105`}>
            <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none mb-1 truncate">{label}</p>
            <div className="flex items-center justify-between">
                <p className="text-sm font-black text-slate-900 leading-none tracking-tight">{value}</p>
                <div className="flex items-center gap-1">
                    {secondaryStats?.map((stat: any, idx: number) => (
                        <div key={idx} className={`flex items-center gap-1 px-1 py-0.5 rounded ${stat.bg || 'bg-orange-50'} border ${stat.border || 'border-orange-100'}`}>
                            <span className={`text-[8px] font-black ${stat.color || 'text-orange-600'}`}>{stat.value}</span>
                            <span className={`text-[6px] font-black ${stat.labelColor || 'text-orange-400'} uppercase tracking-tighter`}>{stat.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }: any) => (
    <div className="flex items-center justify-between mb-2 shrink-0 px-0.5">
        <div className="flex items-center gap-1.5">
            <div className="p-1 rounded bg-slate-100 text-slate-600 group-hover:text-orange-600 transition-colors">
                <Icon className="w-3 h-3" />
            </div>
            <div>
                <h3 className="text-[8.5px] font-black text-slate-900 uppercase tracking-widest leading-tight">{title}</h3>
                {subtitle && <p className="text-[7.5px] text-slate-400 font-bold uppercase tracking-tight">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

const CompactTable = ({ headers, data, renderRow, emptyMessage, pagination }: any) => (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-1 px-2 py-2 border-b border-slate-100 bg-slate-50/50">
            {headers.map((h: any, i: number) => (
                <div key={i} className={`${h.className} text-[8px] font-black text-slate-400 uppercase tracking-widest`}>{h.label}</div>
            ))}
        </div>
        <div className="flex-1 overflow-y-auto custom-scroll">
            {data?.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center opacity-30 gap-1.5 py-8">
                    <Activity className="w-6 h-6 text-slate-400" />
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{emptyMessage || "No Records Data"}</span>
                </div>
            ) : (
                data.map((item: any, i: number) => renderRow(item, i))
            )}
        </div>
        {pagination && (
            <div className="shrink-0 px-2 py-1.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/30">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Page {pagination.page + 1} of {pagination.totalPages || 1}</span>
                <div className="flex gap-1">
                    <button onClick={() => pagination.setPage((p: number) => Math.max(0, p - 1))} disabled={pagination.page === 0} className="p-1 rounded hover:bg-white border border-transparent hover:border-slate-200 disabled:opacity-30"><ChevronLeft className="w-3 h-3" /></button>
                    <button onClick={() => pagination.setPage((p: number) => Math.min(pagination.totalPages - 1, p + 1))} disabled={pagination.page >= pagination.totalPages - 1} className="p-1 rounded hover:bg-white border border-transparent hover:border-slate-200 disabled:opacity-30"><ChevronRight className="w-3 h-3" /></button>
                </div>
            </div>
        )}
    </div>
);

const SidePanel = ({ isOpen, onClose, title, subtitle, icon: Icon, children, width = "w-[600px]" }: any) => (
    <AnimatePresence>
        {isOpen && (
            <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]" />
                <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 200 }} className={`fixed right-0 top-0 h-full ${width} bg-white border-l border-slate-200 z-[101] flex flex-col shadow-2xl`}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-orange-100 text-orange-600"><Icon className="w-5 h-5" /></div>
                            <div>
                                <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest leading-none">{title}</h2>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-1">{subtitle}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="flex-1 min-h-0">{children}</div>
                </motion.div>
            </>
        )}
    </AnimatePresence>
);



// const FleetHealthBreakdownSection = ({ nvrData }: { nvrData: NvrDashboardData | null }) => {
//     const keyToLabel: Record<string, string> = {
//         recording_stopped: "Recording Stopped",
//         overwrite_off_full: "Overwrite Off & Full",
//         ntp_disabled: "NTP Disabled",
//         motion_low_channels: "Motion Low Channels",
//         no_email_alerts: "No Email Alerts",
//         brute_force_off: "Brute Force Off",
//         fully_healthy: "Fully Healthy"
//     };

//     const severityWeight: Record<string, number> = {
//         critical: 4,
//         high: 3,
//         medium: 2,
//         ok: 1
//     };

//     const severityColors: Record<string, { text: string; bg: string; bar: string }> = {
//         critical: { text: "text-rose-600", bg: "bg-rose-50", bar: "bg-rose-500" },
//         high: { text: "text-orange-600", bg: "bg-orange-50", bar: "bg-orange-500" },
//         medium: { text: "text-amber-600", bg: "bg-amber-50", bar: "bg-amber-500" },
//         ok: { text: "text-emerald-600", bg: "bg-emerald-50", bar: "bg-emerald-500" }
//     };

//     const sortedBreakdown = useMemo(() => {
//         if (!nvrData?.fleet_health_breakdown) return [];
//         return Object.entries(nvrData.fleet_health_breakdown)
//             .filter(([key]) => key !== 'recording_stopped' && key !== 'overwrite_off_full' && key !== 'ntp_disabled' && key !== 'brute_force_off')
//             .sort((a, b) => {
//                 const weightA = severityWeight[a[1].severity] || 0;
//                 const weightB = severityWeight[b[1].severity] || 0;
//                 if (weightB !== weightA) {
//                     return weightB - weightA;
//                 }
//                 return b[1].count - a[1].count;
//             });
//     }, [nvrData?.fleet_health_breakdown]);

//     return (
//         <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
//             <div className="flex-1 overflow-y-auto custom-scroll pr-1 mt-1">
//                 {sortedBreakdown.length > 0 ? (
//                     <div className="gap-x-4 gap-y-2">
//                         {sortedBreakdown.map(([key, value]) => {
//                             const colors = severityColors[value.severity] || { text: "text-slate-600", bg: "bg-slate-50", bar: "bg-slate-500" };
//                             return (
//                                 <div key={key} className="space-y-1">
//                                     <div className="flex justify-between items-center text-[8px] font-black uppercase">
//                                         <span className="text-slate-600 tracking-tight truncate max-w-[65%]" title={keyToLabel[key] || key.replace(/_/g, ' ')}>
//                                             {keyToLabel[key] || key.replace(/_/g, ' ')}
//                                         </span>
//                                         <div className="flex items-center gap-1.5 shrink-0">
//                                             <span className={`px-1 py-0.5 rounded-[3px] text-[7px] font-black leading-none ${colors.text} ${colors.bg}`}>
//                                                 {value.count} units
//                                             </span>
//                                             <span className="text-slate-400 font-mono text-[7px]">
//                                                 {value.percent.toFixed(0)}%
//                                             </span>
//                                         </div>
//                                     </div>
//                                     <div className="w-[90%] h-1 bg-slate-100 rounded-full overflow-hidden">
//                                         <div
//                                             className={`h-full rounded-full transition-all duration-500 ${colors.bar}`}
//                                             style={{ width: `${value.percent}%` }}
//                                         />
//                                     </div>
//                                 </div>
//                             );
//                         })}
//                     </div>
//                 ) : (
//                     <div className="h-full flex flex-col items-center justify-center opacity-30 gap-1.5 py-8">
//                         <Shield className="w-6 h-6 text-slate-400" />
//                         <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">No Breakdown Data</span>
//                     </div>
//                 )}
//             </div>
//         </div>
//     );
// };

const mapNvrListItem = (item: any): any => {
    let rawInterfaces = [];
    if (item.network?.interfaces) {
        rawInterfaces = item.network.interfaces;
    } else if (typeof item.network === 'string') {
        try {
            const parsedNet = JSON.parse(item.network);
            if (parsedNet && parsedNet.interfaces) {
                rawInterfaces = parsedNet.interfaces;
            }
        } catch (e) { }
    }

    const dev = typeof item.device === 'string'
        ? (() => { try { return JSON.parse(item.device); } catch (e) { return {}; } })()
        : (item.device || {});

    const net = typeof item.network === 'string'
        ? (() => { try { return JSON.parse(item.network); } catch (e) { return {}; } })()
        : (item.network || {});

    return {
        ...item,
        status: item.status || 'online',
        device: {
            model: dev.model || item.model || '',
            serial_number: dev.serial_number || item.serial_number || '',
            firmware: dev.firmware || item.firmware || '',
            firmware_build: dev.firmware_build || item.firmware_build || '',
        },
        total_channels: item.total_channels ?? (((item.recording?.total_recording ?? 0) + (item.recording?.total_not_recording ?? 0)) || item.total_not_recording || 0),
        total_disks: item.total_disks ?? item.storage?.total_disks ?? 0,
        capacity_tb: item.capacity_tb ?? item.total_space_tb ?? item.storage?.total_space_TB ?? 0,
        total_ok: item.total_ok ?? item.analysis?.total_ok ?? 0,
        total_warnings: item.total_warnings ?? item.analysis?.total_warnings ?? 0,
        total_critical: item.total_critical ?? item.analysis?.total_critical ?? 0,
        network: {
            hostname: net.hostname || item.network?.hostname || '',
            ntp_enabled: !!(net.ntp?.enabled ?? item.network?.ntp?.enabled),
            interfaces: (rawInterfaces || []).map((inf: any) => ({
                name: inf.name || '',
                ip_address: inf.ip_address || '',
                mac_address: inf.mac_address || '',
                dhcp_enabled: !!inf.dhcp_enabled,
            })),
        },
    };
};

const mapNvrDashboardData = (rawData: any): NvrDashboardData => {
    return {
        ...rawData,
        storage: {
            ...rawData.storage,
            low_space_alert_enabled: rawData.storage?.low_space_alert_enabled ?? 0,
            low_space_alert_disabled: rawData.storage?.low_space_alert_disabled ?? 0,
        },
        nvr_list: (rawData.nvr_list || []).map(mapNvrListItem),
    };
};

// --- Main Page ---

const CameraDashboard = () => {
    const navigate = useNavigate();
    const level = useClientLevel();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DashboardData | null>(null);
    const [nvrData, setNvrData] = useState<NvrDashboardData | null>(null);
    const [eventsCount, setEventsCount] = useState<{ total: number; open: number; closed: number } | null>(null);
    const [eventsTrend, setEventsTrend] = useState<any[]>([]);

    // District / Station navigation
    const [searchParams, _setSearchParams] = useSearchParams();
    const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(() => {
        const did = searchParams.get('district_id');
        return did ? Number(did) : null;
    });
    const [selectedDistrictName, setSelectedDistrictName] = useState<string>(() => {
        return searchParams.get('district_name') || '';
    });
    const [selectedStationId, setSelectedStationId] = useState<number | null>(() => {
        const cid = searchParams.get('client_id');
        return cid ? Number(cid) : null;
    });
    const [selectedStationName, setSelectedStationName] = useState<string>(() => {
        return searchParams.get('client_name') || '';
    });

    // Default to region_id = 2, state_id = 1 if not present in URL
    const getInitialLocationIds = (): LocationIds => {
        return {
            regionId: searchParams.get('region_id') ? Number(searchParams.get('region_id')) : 2,
            stateId: searchParams.get('state_id') ? Number(searchParams.get('state_id')) : 1,
            districtId: searchParams.get('district_id') ? Number(searchParams.get('district_id')) : null,
            townId: searchParams.get('town_id') ? Number(searchParams.get('town_id')) : null,
            siteId: searchParams.get('client_id') ? Number(searchParams.get('client_id')) : null,
        };
    };

    const [locationIds, setLocationIds] = useState<LocationIds>(getInitialLocationIds());

    // Update selected station and district when search params change (e.g. on navigation)
    useEffect(() => {
        const cid = searchParams.get('client_id');
        const cname = searchParams.get('client_name');
        const did = searchParams.get('district_id');
        const dname = searchParams.get('district_name');
        setSelectedStationId(cid ? Number(cid) : null);
        setSelectedStationName(cname || '');
        setSelectedDistrictId(did ? Number(did) : null);
        setSelectedDistrictName(dname || '');
    }, [searchParams]);

    // Update location IDs when URL changes
    useEffect(() => {
        const nextIds = {
            regionId: searchParams.get('region_id') ? Number(searchParams.get('region_id')) : 2,
            stateId: searchParams.get('state_id') ? Number(searchParams.get('state_id')) : 1,
            districtId: searchParams.get('district_id') ? Number(searchParams.get('district_id')) : null,
            townId: searchParams.get('town_id') ? Number(searchParams.get('town_id')) : null,
            siteId: searchParams.get('client_id') ? Number(searchParams.get('client_id')) : null,
        };
        setLocationIds(prev => {
            if (
                prev.regionId === nextIds.regionId &&
                prev.stateId === nextIds.stateId &&
                prev.districtId === nextIds.districtId &&
                prev.townId === nextIds.townId &&
                prev.siteId === nextIds.siteId
            ) {
                return prev;
            }
            return nextIds;
        });
    }, [searchParams]);

    // const handleLocationChange = (newIds: LocationIds) => {
    //     const params = new URLSearchParams(searchParams);
    //     if (newIds.regionId) params.set('region_id', String(newIds.regionId));
    //     else params.delete('region_id');
    //     if (newIds.stateId) params.set('state_id', String(newIds.stateId));
    //     else params.delete('state_id');
    //     if (newIds.districtId) params.set('district_id', String(newIds.districtId));
    //     else params.delete('district_id');
    //     if (newIds.townId) params.set('town_id', String(newIds.townId));
    //     else params.delete('town_id');
    //     if (newIds.siteId) params.set('site_id', String(newIds.siteId));
    //     else params.delete('site_id');
    //     setSearchParams(params);
    // };

    // const activeClientId = useMemo(() => {
    //     if (locationIds.siteId && locationIds.siteId !== 'all') {
    //         return locationIds.siteId;
    //     }
    //     if (level && level.name !== 'global') {
    //         return level.id || level.client_id;
    //     }
    //     return null;
    // }, [locationIds.siteId, level]);

    // Filters & States
    const [selectedTrendId, setSelectedTrendId] = useState<number | null>(null);
    const [activeAlertTab, setActiveAlertTab] = useState<'open' | 'closed'>('open');
    const [selectedAlert, setSelectedAlert] = useState<any | null>(null);

    const [panelOpen, setPanelOpen] = useState<string | null>(null);
    const [clientsData, setClientsData] = useState<any[]>([]);

    // Camera List Sidebar Status (to pass as initialStatus)
    const [cameraListStatus, setCameraListStatus] = useState<'all' | 'active' | 'offline'>('all');

    // NVR List Sidebar Filter
    const [nvrListFilter, setNvrListFilter] = useState<'all' | 'healthy'>('all');

    // Event List Sidebar Status (to pass as initialStatus)
    const [eventListStatus, setEventListStatus] = useState<'all' | 'open' | 'closed'>('all');

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const queryObj: any = {};
                if (locationIds.regionId) queryObj.region_id = locationIds.regionId;
                if (locationIds.stateId) queryObj.state_id = locationIds.stateId;
                if (locationIds.districtId) queryObj.district_id = locationIds.districtId;
                if (locationIds.townId) queryObj.town_id = locationIds.townId;
                if (locationIds.siteId) queryObj.id = locationIds.siteId;

                const response = await baseapi.get('/clients', {
                    params: {
                        limit: 1000,
                        query: JSON.stringify(queryObj)
                    }
                });
                if (response.data?.status && response.data?.data) {
                    const mappedData = response.data.data
                        .filter((c: any) => c.latitude != null && c.longitude != null)
                        .map((c: any) => ({
                            coordinate: [c.latitude, c.longitude] as [number, number],
                            id: c.id,
                            name: c.name,
                            is_offline: !!c.is_offline,
                            display_name: c.display_name
                        }));
                    setClientsData(mappedData);
                }
            } catch (error) { }
        };
        fetchClients();
    }, [locationIds]);

    const [pages, setPages] = useState({ uptime: 0, flapping: 0, alerts: 0, reliability: 0, panel: 0 });

    const PANEL_ROWS = 12;

    const stationName = useMemo(() => {
        if (level.name !== 'global') {
            return level.display_name || level.name;
        }
        if (selectedStationName) {
            return selectedStationName;
        }
        if (selectedStationId) {
            const client = clientsData.find(c => c.id === selectedStationId);
            return client?.display_name || client?.name || `Station #${selectedStationId}`;
        }
        return '';
    }, [level, selectedStationName, selectedStationId, clientsData]);

    const districtName = useMemo(() => {
        return selectedDistrictName || (selectedDistrictId ? `District #${selectedDistrictId}` : '');
    }, [selectedDistrictName, selectedDistrictId]);

    const headerTitle = useMemo(() => {
        if (stationName) {
            return `${stationName} Camera Dashboard`;
        }
        if (districtName) {
            return `${districtName} District Camera Dashboard`;
        }
        return "Camera Dashboard";
    }, [stationName, districtName]);

    const activeClientId = useMemo(() => {
        if (selectedStationId) {
            return selectedStationId;
        }
        if (level.name !== 'global' && level.id != null) {
            return level.id;
        }
        return null;
    }, [selectedStationId, level]);

    // Navigation handlers
    const handleBackToMainPage = () => {
        navigate('/dashboard');
    };

    const handleBackToPrevious = () => {
        if (selectedDistrictId) {
            navigate(`/dashboard?district_id=${selectedDistrictId}`);
        } else {
            navigate('/dashboard');
        }
    };

    useEffect(() => { if (level) fetchData(); }, [level, locationIds, selectedDistrictId, selectedStationId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (level.name !== 'global' && level.id != null) {
                params.client_id = level.id;
            }
            if (locationIds.regionId != null) params.region_id = locationIds.regionId;
            if (locationIds.stateId != null) params.state_id = locationIds.stateId;
            if (locationIds.districtId != null) params.district_id = locationIds.districtId;
            if (locationIds.townId != null) params.town_id = locationIds.townId;
            if (locationIds.siteId != null && locationIds.siteId !== 'all') {
                params.client_id = locationIds.siteId;
            }

            // Apply district/station filter
            if (selectedDistrictId) {
                params.district_id = selectedDistrictId;
            }
            if (selectedStationId) {
                params.client_id = selectedStationId;
            }

            const res = await baseapi.get("/dashboard/camera_unified_dashboard", { params });
            const nvrRes = await baseapi.get("/nvr_audit/nvr_unified_dashboard", {
                params: {
                    ...params,
                    region_id: 2,
                    state_id: 1
                }
            });

            // Fetch events count summary
            try {
                const eventsRes = await baseapi.get("/dashboard/events_count", { params });
                if (eventsRes?.data?.status && eventsRes.data.data?.summary) {
                    setEventsCount(eventsRes.data.data.summary);
                }
            } catch (_) { /* silently ignore events_count failure */ }

            // Fetch events trend
            try {
                const trendRes = await baseapi.get("/dashboard/events_trend", { params });
                if (trendRes?.data?.status && trendRes.data.data?.daily_trend) {
                    setEventsTrend(trendRes.data.data.daily_trend);
                }
            } catch (_) { /* silently ignore events_trend failure */ }

            if (res?.data?.status) {
                const newData = res.data.data;
                setData(newData);

                const hasSiteId = locationIds.siteId && locationIds.siteId !== 'all';
                const trendData = newData.trend_analysis || newData;
                const trends = hasSiteId
                    ? trendData.camera_downtime_trend || []
                    : trendData.clients_uptime_trend || [];

                if (trends.length > 0) {
                    setSelectedTrendId(hasSiteId ? trends[0].asset_id : trends[0].client_id);
                }
            }
            if (nvrRes?.data?.status) {
                setNvrData(mapNvrDashboardData(nvrRes.data.data));
            }
        } catch (e) { showError("Failed to fetch dashboard"); }
        finally { setLoading(false); }
    };

    // --- Memoized Data ---
    const reachabilityData = useMemo(() => {
        if (!data?.overview) return [];
        return [
            { name: "Online", value: data.overview.reachable, color: "#10b981" },
            { name: "Offline", value: data.overview.unreachable, color: "#f43f5e" }
        ];
    }, [data]);

    const dailyEventsData = useMemo(() => {
        const events = data?.trend_analysis?.daily_events_30d || data?.daily_events_30d || [];

        if (!events) return [];

        return events.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((e: any) => ({
            date: new Date(e.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
            incidents: e.offline_events,
            persistence: e.still_offline_count,
            impact: e.cameras_affected
        }));
    }, [data]);



    const healthTrendData = useMemo(() => {
        return (data?.health_timeseries || []).map(h => ({
            time: new Date(h.hour).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            health: h.online_percentage,
            reachable: h.cameras_online,
            offline: h.cameras_offline,
            total: h.total_cameras
        }));
    }, [data]);

    // const storageAuditData = useMemo(() => [
    //     { name: 'Overwrite ON', value: nvrData?.storage?.overwrite_enabled || 0, fill: '#10b981' },
    //     { name: 'Overwrite OFF', value: nvrData?.storage?.overwrite_disabled || 0, fill: '#f43f5e' },
    //     { name: 'Retention Set', value: nvrData?.storage?.min_retention_set || 0, fill: '#10b981' },
    //     { name: 'No Retention', value: nvrData?.storage?.min_retention_none || 0, fill: '#f43f5e' },
    //     { name: 'Disk Health OK', value: nvrData?.storage?.disk_health_ok || 0, fill: '#10b981' },
    //     { name: 'Disk Problems', value: (nvrData?.storage?.disk_health_not_ok || 0) + (nvrData?.storage?.disk_errors || 0), fill: '#f43f5e' },
    //     { name: 'Failure Alert ON', value: nvrData?.storage?.failure_alert_enabled || 0, fill: '#10b981' },
    //     { name: 'Failure Alert OFF', value: nvrData?.storage?.failure_alert_disabled || 0, fill: '#f43f5e' },
    //     { name: 'Missing Disk ON', value: nvrData?.storage?.missing_disk_alert_enabled || 0, fill: '#10b981' },
    //     { name: 'Missing Disk OFF', value: nvrData?.storage?.missing_disk_alert_disabled || 0, fill: '#f43f5e' },
    //     { name: 'Low Space ON', value: nvrData?.storage?.low_space_alert_enabled || 0, fill: '#10b981' },
    //     { name: 'Low Space OFF', value: nvrData?.storage?.low_space_alert_disabled || 0, fill: '#f43f5e' },
    // ], [nvrData?.storage]);

    const eventsTrendData = useMemo(() => {
        return eventsTrend
            .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map((d: any) => ({
                date: new Date(d.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
                total: d.total || 0,
                open: d.open || 0,
                closed: d.closed || 0,
            }));
    }, [eventsTrend]);

    const performanceTrendData = useMemo(() => {
        // Switch between Client Performance and Camera Performance based on siteId filter
        const hasSiteId = locationIds.siteId && locationIds.siteId !== 'all';
        const trendData = (data?.trend_analysis || data) as any;
        const trendDataList = hasSiteId
            ? trendData?.camera_downtime_trend
            : trendData?.clients_uptime_trend;

        if (!selectedTrendId || !trendDataList) return [];

        const match = trendDataList.find((item: any) =>
            (hasSiteId ? item.asset_id : item.client_id) === selectedTrendId
        );

        if (!match) return [];

        return (match.trend || []).map((d: any) => ({
            date: d.date ? new Date(d.date).toLocaleDateString([], { month: 'short', day: 'numeric' }) : "—",
            uptime: d.avg_uptime_pct ?? d.uptime_pct ?? 0,
            downtime_min: d.total_downtime_minutes ?? d.downtime_minutes ?? 0,
            downtime: 100 - (d.avg_uptime_pct ?? d.uptime_pct ?? 0)
        }));
    }, [selectedTrendId, data, locationIds.siteId]);

    const trendOptions = useMemo(() => {
        const hasSiteId = locationIds.siteId && locationIds.siteId !== 'all';
        const trendData = (data?.trend_analysis || data) as any;
        const trendDataList = hasSiteId
            ? trendData?.camera_downtime_trend
            : trendData?.clients_uptime_trend;

        return (trendDataList || []).map((item: any) => ({
            label: hasSiteId ? item.camera_name : item.client_name,
            value: hasSiteId ? item.asset_id : item.client_id
        }));
    }, [data, locationIds.siteId]);

    // const reliabilityCameras = useMemo(() => {
    //     return [...(data?.reliability_dashboard?.cameras || [])].sort((a, b) => b.total_offline_events - a.total_offline_events);
    // }, [data]);

    if (loading) return (
        <div className="flex-1 flex min-h-screen flex-col items-center justify-center bg-slate-50 gap-3">
            <div className="relative">
                <div className="w-12 h-12 border-4 border-[#422568]/10 border-t-[#422568] rounded-full animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center"><LayoutDashboard className="w-4 h-4 text-[#422568] animate-pulse" /></div>
            </div>
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.3em] animate-pulse">Syncing Intelligence</p>
        </div>
    );

    return (
        <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden font-sans pb-20">
            {/* Header — single bar: Label | scrollable pills | Refresh */}
            <div className="min-h-10 py-1 bg-white border-b border-slate-200 flex items-center gap-2 px-4 shrink-0 relative z-10">
                {/* Left: Label (pinned) */}
                <div className="flex items-center gap-2 shrink-0">
                    <div className="p-1.5 bg-[#422568] rounded-lg text-white shadow-lg shadow-[#422568]/20">
                        <LayoutDashboard className="w-4 h-4" />
                    </div>
                    <h1 className="text-[13px] font-black text-slate-900 uppercase tracking-widest leading-none whitespace-nowrap">{headerTitle}</h1>
                    <div className="w-px h-5 bg-slate-200 mx-0.5 shrink-0" />
                </div>

                {/* Middle: Spacer */}
                <div className="flex-1" />

                {/* Right: Actions */}
                <div className="shrink-0 flex items-center gap-1.5">
                    {activeClientId !== null && (
                        <button
                            onClick={() => window.location.href = `https://dev.ovulscanner.com/client-details?client_id=${activeClientId}&path=dashboard`}
                            className="px-2.5 py-1.5 bg-[#422568] hover:bg-[#422568]/90 text-white rounded-lg transition-all font-black text-[8px] uppercase tracking-wider shadow-sm"
                        >
                            Station Details
                        </button>
                    )}

                    <button
                        onClick={handleBackToMainPage}
                        className="flex items-center gap-1 px-2.5 py-1 bg-[#422568]/5 hover:bg-[#422568]/5 border border-[#422568]/30 hover:border-[#422568]/30 rounded-lg text-[#422568] hover:text-[#422568] transition-all font-black text-[8px] uppercase tracking-wider shadow-sm"
                    >
                        <ChevronLeft className="w-2.5 h-2.5" /> Back to Main Page
                    </button>

                    {selectedDistrictId !== null && (
                        <button
                            onClick={handleBackToPrevious}
                            className="flex items-center gap-1 px-2.5 py-1 bg-[#422568]/5 hover:bg-[#422568]/5 border border-[#422568]/30 hover:border-[#422568]/30 rounded-lg text-[#422568] hover:text-[#422568] transition-all font-black text-[8px] uppercase tracking-wider shadow-sm"
                        >
                            <ChevronLeft className="w-2.5 h-2.5" /> Back to Stations Overview
                        </button>
                    )}

                    <button onClick={fetchData} className="flex items-center gap-1.5 px-2 py-0.5 h-7.5 bg-orange-50 hover:bg-orange-50 border border-orange-500 hover:border-orange-500 rounded-lg text-orange-500 hover:text-orange-600 transition-all font-black text-[8px] uppercase">
                        <RefreshCw className="w-2.5 h-2.5" /> Refresh
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 custom-scroll space-y-2">


                {/* ROW 1: STATS (Configured for 7 per row) */}
                <div className="grid grid-cols-7 gap-2">
                    <StatCard
                        icon={Cctv}
                        label="Cameras"
                        value={data?.overview?.total}
                        color="text-slate-600"
                        bg="bg-slate-100"
                        onClick={() => {
                            setCameraListStatus('all');
                            setPanelOpen('camera-list');
                        }}
                    />
                    <StatCard
                        icon={Wifi}
                        label="Online"
                        value={data?.overview?.reachable}
                        color="text-emerald-600"
                        bg="bg-emerald-50"
                        onClick={() => {
                            setCameraListStatus('active');
                            setPanelOpen('camera-list');
                        }}
                    />
                    <StatCard
                        icon={WifiOff}
                        label="Offline"
                        value={data?.overview?.unreachable}
                        color="text-rose-600"
                        bg="bg-rose-50"
                        onClick={() => {
                            setCameraListStatus('offline');
                            setPanelOpen('camera-list');
                        }}
                    />
                    <StatCard icon={HeartPulse} label="Health" value={`${data?.overview?.health_percentage?.toFixed(0) || 0}%`} color="text-blue-600" bg="bg-blue-50" />
                    <StatCard
                        icon={Building2}
                        label="Sites"
                        value={data?.clients?.total || 0}
                        color="text-indigo-600"
                        bg="bg-indigo-50"
                        onClick={() => {
                            setPanelOpen('site-list');
                        }}
                    />

                    {/* Fleet Stats (New) */}
                    <StatCard
                        icon={Server}
                        label="Total NVRs"
                        value={nvrData?.device_overview?.total_nvrs || 0}
                        color="text-blue-600"
                        bg="bg-blue-50"
                        onClick={() => {
                            setNvrListFilter('all');
                            setPanelOpen('nvr-list');
                        }}
                    />
                    <StatCard
                        icon={Database}
                        label="Healthy NVRs"
                        value={nvrData?.fleet_stats?.healthy_nvrs || 0}
                        color="text-emerald-600"
                        bg="bg-emerald-50"
                        onClick={() => {
                            setNvrListFilter('healthy');
                            setPanelOpen('nvr-list');
                        }}
                    />
                    <StatCard
                        icon={Activity}
                        label="Fleet Score"
                        value={`${nvrData?.fleet_stats?.fleet_health_score?.toFixed(1) || 0}%`}
                        color="text-amber-600"
                        bg="bg-amber-50"
                    />
                    {/* <StatCard
                        icon={AlertCircle}
                        label="Recording Stopped"
                        value={nvrData?.fleet_stats?.recording_stopped || 0}
                        color="text-rose-600"
                        bg="bg-rose-50"
                        secondaryStats={[{ value: "RECORDS", label: "" }]}
                    /> */}
                    <StatCard
                        icon={HardDrive}
                        label="Overwrite disabled"
                        value={nvrData?.fleet_stats?.overwrite_disabled_and_full || 0}
                        color="text-rose-600"
                        bg="bg-rose-50"
                    />
                    {/* <StatCard
                        icon={History}
                        label="NTP Off"
                        value={nvrData?.fleet_stats?.ntp_off || 0}
                        color="text-amber-600"
                        bg="bg-amber-50"
                    /> */}
                    {/* <StatCard
                        icon={Shield}
                        label="Brute Off"
                        value={nvrData?.fleet_stats?.brute_force_off || 0}
                        color="text-amber-600"
                        bg="bg-amber-50"
                    /> */}
                    <StatCard
                        icon={WifiOff}
                        label="No Email Alerts"
                        value={nvrData?.fleet_stats?.no_email_alerts || 0}
                        color="text-amber-600"
                        bg="bg-amber-50"
                    />


                    <StatCard
                        icon={Bell}
                        label="Total Events"
                        value={eventsCount?.total ?? '—'}
                        color="text-violet-600"
                        bg="bg-violet-50"
                        onClick={() => {
                            setEventListStatus('all');
                            setPanelOpen('event-list');
                        }}
                    />
                    <StatCard
                        icon={BellRing}
                        label="Open Events"
                        value={eventsCount?.open ?? '—'}
                        color="text-amber-600"
                        bg="bg-amber-50"
                        onClick={() => {
                            setEventListStatus('open');
                            setPanelOpen('event-list');
                        }}
                    />
                    <StatCard
                        icon={BellOff}
                        label="Closed Events"
                        value={eventsCount?.closed ?? '—'}
                        color="text-emerald-600"
                        bg="bg-emerald-50"
                        onClick={() => {
                            setEventListStatus('closed');
                            setPanelOpen('event-list');
                        }}
                    />

                </div>

                {/* ROW 2: PRIMARY CHARTS (Adjusted heights to prevent overlap) */}
                <div className="grid grid-cols-12 gap-3 h-[200px] overflow-hidden shrink-0">
                    <div className="col-span-2 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm group">
                        <SectionHeader icon={Activity} title="Camera Health" />
                        <div className="flex-1 min-h-0 flex flex-col">
                            <div className="flex-1 flex items-center justify-center relative">
                                <ResponsiveContainer width="100%" height={100}>
                                    <PieChart>
                                        <Pie data={reachabilityData} innerRadius="70%" outerRadius="95%" paddingAngle={4} dataKey="value" cornerRadius={6}>
                                            {reachabilityData.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
                                            <Label content={({ viewBox: { cx, cy } }: any) => (
                                                <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                                    <tspan x={cx} dy="-0.1em" fill="#0f172a" fontSize="10" fontWeight="900">{data?.overview?.total || 0}</tspan>
                                                    <tspan x={cx} dy="1.4em" fill="#64748b" fontSize="5" fontWeight="900" style={{ textTransform: 'uppercase' }}>Units</tspan>
                                                </text>
                                            )} />
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-1 flex flex-col gap-1 border-t border-slate-50 pt-1.5 overflow-hidden">
                                {reachabilityData.map((e, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-1">
                                            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: e.color }} />
                                            <span className="text-[8px] font-black text-slate-500 uppercase">{e.name}</span>
                                        </div>
                                        <span className="text-[8px] font-black text-slate-900">{e.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="col-span-3 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm">
                        <SectionHeader icon={TrendingUp} title="30D Stability Trend" />
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={dailyEventsData} margin={{ top: 5, right: 10, left: -30, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="incidentGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="impactGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="4 4" stroke="#f1f5f9" vertical={false} />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6, fontWeight: '900' }} interval={6} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6, fontWeight: '900' }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area type="monotone" dataKey="incidents" stroke="#f43f5e" strokeWidth={1.5} fill="url(#incidentGrad)" name="INCIDENTS" dot={false} activeDot={{ r: 3 }} />
                                    <Area type="monotone" dataKey="impact" stroke="#6366f1" strokeWidth={1.5} fill="url(#impactGrad)" name="IMPACT" dot={false} activeDot={{ r: 3 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* EVENT TRENDS CHART */}
                    <div className="col-span-4 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm max-h-[200px]">
                        <SectionHeader icon={Bell} title="Event Trends" subtitle="Daily overview" />
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={eventsTrendData} margin={{ top: 5, right: 5, bottom: 0, left: -15 }}>
                                    <defs>
                                        <linearGradient id="eventTotalGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                            <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="eventOpenGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                                            <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="eventClosedGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 7, fontWeight: '900' }} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6, fontWeight: '900' }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={1.5} fill="url(#eventTotalGrad)" name="TOTAL" dot={false} activeDot={{ r: 3 }} />
                                    <Area type="monotone" dataKey="open" stroke="#f59e0b" strokeWidth={1.5} fill="url(#eventOpenGrad)" name="OPEN" dot={false} activeDot={{ r: 3 }} />
                                    <Area type="monotone" dataKey="closed" stroke="#10b981" strokeWidth={1.5} fill="url(#eventClosedGrad)" name="CLOSED" dot={false} activeDot={{ r: 3 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="col-span-3 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm">
                        <SectionHeader
                            icon={Database}
                            title="NVR Health"
                            action={<button onClick={() => setPanelOpen('nvr-issues')} className="text-[7.5px] font-black text-blue-600 uppercase hover:text-blue-700 transition-colors">Details →</button>}
                        />
                        <div className="flex-1 min-h-0 flex flex-col justify-between">
                            <div className="space-y-2">
                                {/* <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-[7.5px] font-black text-slate-500 uppercase tracking-widest">Recording Rate</span>
                                        <span className={`text-[9px] font-black ${(nvrData?.recording?.recording_rate_percent || 0) > 90 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                            {nvrData?.recording?.recording_rate_percent || 0}%
                                        </span>
                                    </div>
                                    <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-500 ${(nvrData?.recording?.recording_rate_percent || 0) > 90 ? 'bg-emerald-500' : 'bg-rose-500'}`}
                                            style={{ width: `${nvrData?.recording?.recording_rate_percent || 0}%` }}
                                        />
                                    </div>
                                </div> */}
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="p-1.5 bg-rose-50 border border-rose-100 rounded-lg">
                                        <div className="flex justify-between items-start mb-0.5">
                                            <p className="text-[7px] font-black text-rose-400 uppercase leading-none">Critical</p>
                                            <AlertCircle className="w-2 h-2 text-rose-300" />
                                        </div>
                                        <p className="text-sm font-black text-rose-600 leading-none">{nvrData?.health?.total_critical || 0}</p>
                                    </div>
                                    <div className="p-1.5 bg-amber-50 border border-amber-100 rounded-lg">
                                        <div className="flex justify-between items-start mb-0.5">
                                            <p className="text-[7px] font-black text-amber-400 uppercase leading-none">Warning</p>
                                            <Activity className="w-2 h-2 text-amber-300" />
                                        </div>
                                        <p className="text-sm font-black text-amber-600 leading-none">{nvrData?.health?.total_warnings || 0}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-2 space-y-1.5 pt-2 border-t border-slate-50">
                                <div className="flex items-center justify-between text-[8px] font-black">
                                    <div className="flex items-center gap-1.5">
                                        <div className={`w-1.5 h-1.5 rounded-full ${(nvrData?.storage?.disk_errors || 0) > 0 ? 'bg-rose-500 animate-pulse' : 'bg-emerald-500'}`} />
                                        <span className="text-slate-500 uppercase tracking-tight">Disk Health</span>
                                    </div>
                                    <span className={(nvrData?.storage?.disk_errors || 0) > 0 ? "text-rose-600" : "text-emerald-600"}>
                                        {(nvrData?.storage?.disk_errors || 0) > 0 ? `${nvrData?.storage?.disk_errors} Err` : 'Healthy'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-[8px] font-black">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
                                        <span className="text-slate-500 uppercase tracking-tight">Active Channels</span>
                                    </div>
                                    <span className="text-slate-900">{nvrData?.recording?.total_channels_recording} / {nvrData?.device_overview?.total_channels}</span>
                                </div>
                                <div className="flex items-center justify-between text-[8px] font-black">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
                                        <span className="text-slate-500 uppercase tracking-tight">Total Disks</span>
                                    </div>
                                    <span className="text-slate-900">
                                        {(nvrData?.storage?.total_disks || 0)}
                                    </span>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                {/* CONDITIONAL LAYOUT BASED ON SITE SELECTION */}
                {(() => {
                    const hasSiteId = locationIds.siteId && locationIds.siteId !== 'all';

                    if (hasSiteId) {
                        return (
                            <div className="grid grid-cols-12 gap-3">
                                {/* MAP (Span 2 rows) */}
                                <div className="col-span-4 row-span-2 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm h-[400px]">
                                    <SectionHeader icon={MapIcon} title="Deployment Site Map" subtitle="Live Camera Distribution" />
                                    <div className="flex-1 min-h-0 mt-2">
                                        <MiniMap clients={clientsData} selectedDistrictName={selectedDistrictName} />
                                    </div>
                                </div>

                                {/* CAMERA PERFORMANCE (Row 2) */}
                                <div className="col-span-4 bg-white border border-slate-200 rounded-lg p-2 flex flex-col shadow-sm relative overflow-hidden h-[200px] shrink-0">
                                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
                                        <div className="w-36">
                                            <SingleSelectLocalSearchDropdown
                                                options={trendOptions}
                                                value={selectedTrendId}
                                                onChange={(val) => setSelectedTrendId(Number(val))}
                                                placeholder="Search Camera..."
                                                buttonClassName="!py-1 !pl-2.5 !pr-8 !rounded-lg !bg-slate-50 !border-slate-100 !text-[8.5px] !h-7 !min-h-0"
                                                disableClear={true}
                                            />
                                        </div>
                                    </div>
                                    <SectionHeader
                                        icon={Cctv}
                                        title="Camera Performance"
                                        subtitle="Isolated Availability Profiling"
                                    />
                                    <div className="flex-1 min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={performanceTrendData} margin={{ top: 5, right: 5, left: -40, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6.3, fontWeight: '900' }} interval={4} />
                                                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6.3, fontWeight: '900' }} />
                                                <Tooltip content={({ active, payload, label }: any) => {
                                                    if (active && payload && payload.length) {
                                                        const d = payload[0].payload;
                                                        return (
                                                            <div className="bg-white/95 backdrop-blur-sm border border-slate-200 p-1.5 rounded shadow-xl z-50 min-w-[110px]">
                                                                <p className="text-slate-900 text-[8.5px] font-black mb-1 px-1 uppercase tracking-widest">{label}</p>
                                                                <div className="flex items-center justify-between gap-3 text-[8px] px-1 font-black mb-0.5">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                                                        <span className="text-slate-500 uppercase">Uptime</span>
                                                                    </div>
                                                                    <span className="text-slate-900">{d.uptime.toFixed(1)}%</span>
                                                                </div>
                                                                <div className="flex items-center justify-between gap-3 text-[8px] px-1 font-black">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                                                                        <span className="text-slate-500 uppercase">Down</span>
                                                                    </div>
                                                                    <span className="text-rose-600">{d.downtime_min}m</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }} />
                                                <Area type="monotone" dataKey="uptime" stroke="#3b82f6" strokeWidth={2} fill="url(#perfGrad)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* 24H HEALTH TRENDS (Row 3) */}
                                <div className="col-span-4 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm h-[200px] shrink-0 overflow-hidden">
                                    <SectionHeader icon={History} title="24H Health Trends" subtitle="Hourly health distribution" />
                                    <div className="flex-1 min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={healthTrendData} margin={{ top: 5, right: 0, left: -35, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="healthGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2} />
                                                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6.3, fontWeight: '900' }} interval={6} />
                                                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6.3, fontWeight: '900' }} />
                                                <Tooltip content={({ active, payload, label }: any) => {
                                                    if (active && payload && payload.length) {
                                                        const d = payload[0].payload;
                                                        return (
                                                            <div className="bg-white/95 backdrop-blur-sm border border-slate-200 p-2 rounded shadow-xl z-50 min-w-[120px]">
                                                                <p className="text-slate-900 text-[8.5px] font-black mb-1.5 uppercase tracking-widest">{label}</p>
                                                                <div className="flex justify-between items-center gap-3 text-[8px] font-black mb-1 px-0.5">
                                                                    <span className="text-slate-500 uppercase">Total Units</span>
                                                                    <span className="text-slate-900">{d.total}</span>
                                                                </div>
                                                                <div className="flex justify-between items-center gap-3 text-[8px] font-black mb-1 px-0.5">
                                                                    <span className="text-emerald-600 uppercase">Online</span>
                                                                    <span className="text-slate-900">{d.reachable}</span>
                                                                </div>
                                                                <div className="flex justify-between items-center gap-3 text-[8px] font-black mb-2 px-0.5">
                                                                    <span className="text-rose-600 uppercase">Offline</span>
                                                                    <span className="text-slate-900">{d.offline}</span>
                                                                </div>
                                                                <div className="pt-1.5 border-t border-slate-100 flex justify-between items-center gap-3 px-1 mt-1">
                                                                    <span className="text-[9px] font-black text-blue-600 uppercase">Health</span>
                                                                    <span className="text-xs font-black text-slate-900 tracking-tight">{d.health.toFixed(1)}%</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }} />
                                                <Area type="monotone" dataKey="health" stroke="#06b6d4" strokeWidth={2} fill="url(#healthGrad)" dot={{ r: 3, fill: '#06b6d4', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* EVENTS TABLE (Row 3) */}
                                <div className="col-span-8 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm group overflow-hidden h-[190px]">
                                    <div className="flex items-center justify-between mb-2 shrink-0">
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => setActiveAlertTab('open')} className={`px-2 py-1 rounded text-[8px] font-black uppercase transition-all ${activeAlertTab === 'open' ? 'bg-rose-50 text-rose-600 border border-rose-100 shadow-sm' : 'text-slate-400'}`}>Open Events ({data?.alerts?.open_alerts?.length || 0})</button>
                                            <button onClick={() => setActiveAlertTab('closed')} className={`px-2 py-1 rounded text-[8px] font-black uppercase transition-all ${activeAlertTab === 'closed' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm' : 'text-slate-400'}`}>Resolved</button>
                                        </div>
                                        <button onClick={() => setPanelOpen('alerts')} className="text-[7px] font-black text-orange-600 uppercase">More →</button>
                                    </div>
                                    <CompactTable
                                        headers={[
                                            { label: "Camera", className: "col-span-3" },
                                            { label: "Event", className: "col-span-3" },
                                            { label: "Diagnostic Message", className: "col-span-4" },
                                            { label: "Time", className: "col-span-2 text-right" }
                                        ]}
                                        data={(activeAlertTab === 'open' ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || [])).slice(pages.alerts * 5, (pages.alerts + 1) * 5)}
                                        pagination={{ page: pages.alerts, totalPages: Math.ceil((activeAlertTab === 'open' ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || [])).length / 5), setPage: (p: any) => setPages(prev => ({ ...prev, alerts: typeof p === 'function' ? p(prev.alerts) : p })) }}
                                        renderRow={(alert: any, i: number) => (
                                            <div key={i} onClick={() => setSelectedAlert(alert)} className="grid grid-cols-12 gap-1 px-1 py-1.5 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-all items-center">
                                                <div className="col-span-3 text-[8px] font-black text-blue-700 truncate">{alert.camera_name || alert.ip_address}</div>
                                                <div className="col-span-3 truncate text-[7px] font-black uppercase text-rose-600 px-1 truncate block">{alert.event_type.replace(/_/g, ' ')}</div>
                                                <div className="col-span-4 truncate text-[7px] font-black uppercase text-slate-900 px-1 truncate block">{alert.message.replace(/_/g, ' ')}</div>
                                                <div className="col-span-2 text-right text-[7px] font-black text-slate-400 font-mono">{new Date(alert.updated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </div>
                                        )}
                                    />
                                </div>

                                {/* FLEET HEALTH BREAKDOWN (Row 3)
                                <div className="col-span-4 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm overflow-hidden h-[190px]">
                                    <SectionHeader icon={Shield} title="Fleet Health Breakdown" subtitle="System anomaly analysis" />
                                    <FleetHealthBreakdownSection nvrData={nvrData} />
                                </div> */}
                            </div>
                        );
                    } else {
                        // SITE NOT SELECTED
                        return (
                            <div className="grid grid-cols-12 gap-2">
                                {/* MAP (Span 2 rows, Wider - 6 cols) */}
                                <div className="col-span-5 row-span-2 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm h-[400px]">
                                    <SectionHeader icon={MapIcon} title="Deployment Site Map" subtitle="Live Client Site Distribution" />
                                    <div className="flex-1 min-h-0 mt-2">
                                        <MiniMap clients={clientsData} selectedDistrictName={selectedDistrictName} />
                                    </div>
                                </div>

                                {/* 24H HEALTH TRENDS (Row 2, 6 cols) */}
                                <div className="col-span-7 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm h-[200px]">
                                    <SectionHeader icon={History} title="24H Health Trends" subtitle="Hourly health distribution" />
                                    <div className="flex-1 min-h-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={healthTrendData} margin={{ top: 5, right: 0, left: -35, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="healthGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2} />
                                                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6.3, fontWeight: '900' }} interval={6} />
                                                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 6.3, fontWeight: '900' }} />
                                                <Tooltip content={({ active, payload, label }: any) => {
                                                    if (active && payload && payload.length) {
                                                        const d = payload[0].payload;
                                                        return (
                                                            <div className="bg-white/95 backdrop-blur-sm border border-slate-200 p-2 rounded shadow-xl z-50 min-w-[120px]">
                                                                <p className="text-slate-900 text-[8.5px] font-black mb-1.5 uppercase tracking-widest">{label}</p>
                                                                <div className="flex justify-between items-center gap-3 text-[8px] font-black mb-1 px-0.5">
                                                                    <span className="text-slate-500 uppercase">Total Units</span>
                                                                    <span className="text-slate-900">{d.total}</span>
                                                                </div>
                                                                <div className="flex justify-between items-center gap-3 text-[8px] font-black mb-1 px-0.5">
                                                                    <span className="text-emerald-600 uppercase">Online</span>
                                                                    <span className="text-slate-900">{d.reachable}</span>
                                                                </div>
                                                                <div className="flex justify-between items-center gap-3 text-[8px] font-black mb-2 px-0.5">
                                                                    <span className="text-rose-600 uppercase">Offline</span>
                                                                    <span className="text-slate-900">{d.offline}</span>
                                                                </div>
                                                                <div className="pt-1.5 border-t border-slate-100 flex justify-between items-center gap-3 px-1 mt-1">
                                                                    <span className="text-[9px] font-black text-blue-600 uppercase">Health</span>
                                                                    <span className="text-xs font-black text-slate-900 tracking-tight">{d.health.toFixed(1)}%</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }} />
                                                <Area type="monotone" dataKey="health" stroke="#06b6d4" strokeWidth={2} fill="url(#healthGrad)" dot={{ r: 3, fill: '#06b6d4', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* EVENTS TABLE (Row 3, 6 cols) */}
                                <div className="col-span-7 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm group overflow-hidden h-[190px]">
                                    <div className="flex items-center justify-between mb-2 shrink-0">
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => setActiveAlertTab('open')} className={`px-2 py-1 rounded text-[8px] font-black uppercase transition-all ${activeAlertTab === 'open' ? 'bg-rose-50 text-rose-600 border border-rose-100 shadow-sm' : 'text-slate-400'}`}>Open Events ({data?.alerts?.open_alerts?.length || 0})</button>
                                            <button onClick={() => setActiveAlertTab('closed')} className={`px-2 py-1 rounded text-[8px] font-black uppercase transition-all ${activeAlertTab === 'closed' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm' : 'text-slate-400'}`}>Resolved</button>
                                        </div>
                                        <button onClick={() => setPanelOpen('alerts')} className="text-[7px] font-black text-orange-600 uppercase">More →</button>
                                    </div>
                                    <CompactTable
                                        headers={[
                                            { label: "Camera", className: "col-span-3" },
                                            { label: "Event", className: "col-span-3" },
                                            { label: "Diagnostic Message", className: "col-span-4" },
                                            { label: "Time", className: "col-span-2 text-right" }
                                        ]}
                                        data={(activeAlertTab === 'open' ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || [])).slice(pages.alerts * 5, (pages.alerts + 1) * 5)}
                                        pagination={{ page: pages.alerts, totalPages: Math.ceil((activeAlertTab === 'open' ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || [])).length / 5), setPage: (p: any) => setPages(prev => ({ ...prev, alerts: typeof p === 'function' ? p(prev.alerts) : p })) }}
                                        renderRow={(alert: any, i: number) => (
                                            <div key={i} onClick={() => setSelectedAlert(alert)} className="grid grid-cols-12 gap-1 px-1 py-1.5 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-all items-center">
                                                <div className="col-span-3 text-[8px] font-black text-blue-700 truncate">{alert.camera_name || alert.ip_address}</div>
                                                <div className="col-span-3 truncate text-[7px] font-black uppercase text-rose-600 px-1 truncate block">{alert.event_type.replace(/_/g, ' ')}</div>
                                                <div className="col-span-4 truncate text-[7px] font-black uppercase text-slate-900 px-1 truncate block">{alert.message.replace(/_/g, ' ')}</div>
                                                <div className="col-span-2 text-right text-[7px] font-black text-slate-400 font-mono">{new Date(alert.updated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </div>
                                        )}
                                    />
                                </div>

                                {/* FLEET HEALTH BREAKDOWN (Row 3, 6 cols)
                                <div className="col-span-4 bg-white border border-slate-200 rounded-xl p-2.5 flex flex-col shadow-sm overflow-hidden h-[190px]">
                                    <SectionHeader icon={Shield} title="Fleet Health Breakdown" subtitle="System anomaly analysis" />
                                    <FleetHealthBreakdownSection nvrData={nvrData} />
                                </div> */}
                            </div>
                        );
                    }
                })()}
            </div>

            {/* --- Panels --- */}

            <CameraListSidebar
                isOpen={panelOpen === "camera-list"}
                onClose={() => setPanelOpen(null)}
                initialStatus={cameraListStatus}
                locationIds={locationIds}
                level={level}
            />

            <SiteListSidebar
                isOpen={panelOpen === "site-list"}
                onClose={() => setPanelOpen(null)}
                locationIds={locationIds}
                level={level}
            />

            <NvrListSidebar
                isOpen={panelOpen === "nvr-list"}
                onClose={() => setPanelOpen(null)}
                nvrList={nvrData?.nvr_list || []}
                filter={nvrListFilter}
            />

            <EventListSidebar
                isOpen={panelOpen === "event-list"}
                onClose={() => setPanelOpen(null)}
                initialStatus={eventListStatus}
                locationIds={locationIds}
                level={level}
                selectedDistrictId={selectedDistrictId}
                selectedStationId={selectedStationId}
            />

            <SidePanel isOpen={panelOpen === 'uptime'} onClose={() => setPanelOpen(null)} icon={HeartPulse} title="Asset Availability Audit" subtitle="Complete Monitoring Camera Historical Persistence">
                <div className="h-full flex flex-col px-4 py-3">
                    <CompactTable
                        headers={[
                            { label: "#", className: "col-span-1" },
                            { label: "Camera Identification", className: "col-span-5" },
                            { label: "Status", className: "col-span-2 text-center" },
                            { label: "IP Connectivity", className: "col-span-4 text-right" }
                        ]}
                        data={(data?.fleet || data?.uptime?.cameras || []).slice(pages.panel * PANEL_ROWS, (pages.panel + 1) * PANEL_ROWS)}
                        pagination={{ page: pages.panel, totalPages: Math.ceil((data?.fleet || data?.uptime?.cameras || []).length / PANEL_ROWS), setPage: (p: any) => setPages(prev => ({ ...prev, panel: typeof p === 'function' ? p(prev.panel) : p })) }}
                        renderRow={(cam: any, i: number) => (
                            <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-all items-center">
                                <div className="col-span-1 text-[9px] font-bold text-slate-400 font-mono">{pages.panel * PANEL_ROWS + i + 1}</div>
                                <div className="col-span-5">
                                    <p className="text-[10px] font-black text-slate-900 truncate">{cam.camera_name || cam.name}</p>
                                    <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5 tracking-tight">{cam.asset_status}</p>
                                </div>
                                <div className="col-span-2 text-center">
                                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black border uppercase ${(cam.current_uptime_pct ?? cam.uptime_30d_percentage ?? 100) > 95 ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-orange-50 text-orange-600 border-orange-100'}`}>{(cam.current_uptime_pct ?? cam.uptime_30d_percentage ?? 100)?.toFixed(1)}%</span>
                                </div>
                                <div className="col-span-4 text-right text-[9px] font-black text-slate-500 font-mono">{cam.ip_address}</div>
                            </div>
                        )}
                    />
                </div>
            </SidePanel>

            <SidePanel isOpen={panelOpen === 'reliability'} onClose={() => setPanelOpen(null)} icon={Activity} title="Reliability Intelligence Audit" subtitle="In-depth Recovery Metrics & Camera Outage Analysis" width="w-[720px]">
                <div className="h-full flex flex-col px-4 py-3">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Camera Avg Recovery</p>
                            <p className="text-xl font-black text-blue-600">{data?.reliability_dashboard?.stats?.fleet_avg_recovery_minutes?.toFixed(1)}<span className="text-[10px] ml-1 uppercase opacity-60">min</span></p>
                        </div>
                        <div className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Camera Outages</p>
                            <p className="text-xl font-black text-rose-500">{data?.reliability_dashboard?.stats?.cameras_never_recovered}</p>
                        </div>
                        <div className="px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Tracked Population</p>
                            <p className="text-xl font-black text-slate-900">{data?.reliability_dashboard?.stats?.total_cameras_tracked}</p>
                        </div>
                    </div>
                    <CompactTable
                        headers={[
                            { label: "Camera Identification", className: "col-span-6" },
                            { label: "Outages", className: "col-span-1 text-center" },
                            { label: "Avg Recov", className: "col-span-2 text-center" },
                            { label: "Total Down", className: "col-span-2 text-center" },
                            { label: "Status", className: "col-span-1 text-right" }
                        ]}
                        data={(data?.reliability_dashboard?.cameras || []).slice(pages.panel * PANEL_ROWS, (pages.panel + 1) * PANEL_ROWS)}
                        pagination={{ page: pages.panel, totalPages: Math.ceil((data?.reliability_dashboard?.cameras || []).length / PANEL_ROWS), setPage: (p: any) => setPages(prev => ({ ...prev, panel: typeof p === 'function' ? p(prev.panel) : p })) }}
                        renderRow={(cam: any, i: number) => (
                            <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-slate-100 items-center hover:bg-slate-50 transition-all">
                                <div className="col-span-6">
                                    <p className="text-[10px] font-black text-slate-900 truncate">{cam.camera_name}</p>
                                    <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5 tracking-tight">{cam.ip_address} • {cam.location || "Branch N/A"}</p>
                                </div>
                                <div className="col-span-1 text-center text-[9px] font-black text-slate-400 bg-slate-50 rounded py-0.5">{cam.total_offline_events}</div>
                                <div className="col-span-2 text-center text-[10px] font-black text-blue-600 font-mono">{cam.avg_recovery_minutes?.toFixed(1)}m</div>
                                <div className="col-span-2 text-center text-[10px] font-black text-slate-500 font-mono">{cam.total_downtime_hours?.toFixed(1)}h</div>
                                <div className="col-span-1 text-right flex justify-end">
                                    <div className={`w-2 h-2 rounded-full ${cam.asset_status === 'active' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.3)]'}`} />
                                </div>
                            </div>
                        )}
                    />
                </div>
            </SidePanel>

            <SidePanel isOpen={panelOpen === 'flapping'} onClose={() => setPanelOpen(null)} icon={Cpu} title="Flapping Intelligence Audit" subtitle="Unstable Connection Detection & Root Cause Persistence" width="w-[680px]">
                <div className="h-full flex flex-col px-4 py-3">
                    <CompactTable
                        headers={[
                            { label: "Camera Identification", className: "col-span-6" },
                            { label: "Flaps", className: "col-span-2 text-center" },
                            { label: "Downtime", className: "col-span-2 text-center" },
                            { label: "Last Incident", className: "col-span-2 text-right" }
                        ]}
                        data={(data?.flapping || []).slice(pages.panel * PANEL_ROWS, (pages.panel + 1) * PANEL_ROWS)}
                        pagination={{ page: pages.panel, totalPages: Math.ceil((data?.flapping || []).length / PANEL_ROWS), setPage: (p: any) => setPages(prev => ({ ...prev, panel: typeof p === 'function' ? p(prev.panel) : p })) }}
                        renderRow={(cam: any, i: number) => (
                            <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 hover:bg-slate-50 transition-all items-center">
                                <div className="col-span-6">
                                    <p className="text-[10px] font-black text-slate-900 truncate">{cam.camera_name}</p>
                                    <p className="text-[8px] text-slate-400 font-bold font-mono truncate">{cam.ip_address}</p>
                                </div>
                                <div className="col-span-2 text-center">
                                    <span className={`px-2 py-0.5 rounded text-[9px] font-black ${cam.flap_count > 2 ? 'bg-orange-50 text-orange-600' : 'bg-slate-100 text-slate-500'}`}>{cam.flap_count}</span>
                                </div>
                                <div className="col-span-2 text-center text-[10px] font-black text-slate-500 font-mono">{cam.total_downtime_minutes?.toFixed(1)}m</div>
                                <div className="col-span-2 text-right">
                                    <p className="text-[9px] font-black text-slate-500">{cam.last_flap ? new Date(cam.last_flap).toLocaleDateString() : "—"}</p>
                                    {cam.last_flap && <p className="text-[8px] text-slate-400 font-bold">{new Date(cam.last_flap).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>}
                                </div>
                            </div>
                        )}
                    />
                </div>
            </SidePanel>

            <SidePanel isOpen={panelOpen === 'alerts'} onClose={() => setPanelOpen(null)} icon={AlertCircle} title="Historical Events Log" subtitle="Comprehensive Connectivity & Operations Audit Trail" width="w-[750px]">
                <div className="h-full flex flex-col px-4 py-3">
                    <div className="flex items-center gap-2 mb-4 shrink-0">
                        <button onClick={() => { setActiveAlertTab('open'); setPages(p => ({ ...p, panel: 0 })); }} className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${activeAlertTab === 'open' ? 'bg-rose-50 text-rose-600 border border-rose-100 shadow-sm' : 'text-slate-400 hover:bg-slate-50'}`}>Open ({data?.alerts?.open_alerts?.length || 0})</button>
                        <button onClick={() => { setActiveAlertTab('closed'); setPages(p => ({ ...p, panel: 0 })); }} className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${activeAlertTab === 'closed' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-sm' : 'text-slate-400 hover:bg-slate-50'}`}>Resolved ({data?.alerts?.closed_30d?.length || 0})</button>
                    </div>
                    {(() => {
                        const panelAlerts = activeAlertTab === 'open' ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || []);
                        return (
                            <CompactTable
                                headers={[
                                    { label: "Asset", className: "col-span-4" },
                                    { label: "Event Type", className: "col-span-3" },
                                    { label: "Diagnostic Message", className: "col-span-3" },
                                    { label: "Triggered At", className: "col-span-2 text-right" }
                                ]}
                                data={panelAlerts.slice(pages.panel * PANEL_ROWS, (pages.panel + 1) * PANEL_ROWS)}
                                pagination={{ page: pages.panel, totalPages: Math.ceil(panelAlerts.length / PANEL_ROWS), setPage: (p: any) => setPages(prev => ({ ...prev, panel: typeof p === 'function' ? p(prev.panel) : p })) }}
                                renderRow={(alert: any, i: number) => (
                                    <div key={i} onClick={() => setSelectedAlert(alert)} className="grid grid-cols-12 gap-3 px-3 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-all items-start">
                                        <div className="col-span-4 min-w-0">
                                            <p className="text-[10px] font-black text-slate-900 truncate">{alert.camera_name || "Unknown Camera"}</p>
                                            <p className="text-[8px] text-slate-400 font-bold font-mono truncate">{alert.ip_address || "No IP Registered"}</p>
                                        </div>
                                        <div className="col-span-3">
                                            <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded ${alert.event_type.includes('auth') ? 'bg-indigo-50 text-indigo-600' : 'bg-rose-50 text-rose-600'}`}>
                                                {alert.event_type.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        <div className="col-span-3">
                                            <p className="text-[9px] text-slate-500 leading-tight truncate group-hover:whitespace-normal" title={alert.message}>{alert.message}</p>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <p className="text-[9px] font-black text-slate-500">{new Date(alert.updated).toLocaleDateString()}</p>
                                            <p className="text-[8px] text-slate-400 font-bold">{new Date(alert.updated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>
                                    </div>
                                )}
                            />
                        );
                    })()}
                </div>
            </SidePanel>

            <AnimatePresence>
                {selectedAlert && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedAlert(null)} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[200]" />
                        <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 260 }} className="fixed right-0 top-0 h-full w-[480px] bg-white border-l border-slate-200 z-[201] flex flex-col shadow-[-20px_0_50px_rgba(0,0,0,0.05)]">
                            <div className={`px-5 py-4 border-b border-slate-100 flex items-center justify-between ${selectedAlert.event_status === 'open' ? 'bg-rose-50/30' : 'bg-emerald-50/30'}`}>
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-xl ${selectedAlert.event_status === 'open' ? 'bg-rose-100/50 text-rose-600 shadow-[0_0_15px_rgba(244,63,94,0.1)]' : 'bg-emerald-100/50 text-emerald-600 shadow-[0_0_15px_rgba(16,185,129,0.1)]'}`}>
                                        <AlertCircle className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">{selectedAlert.event_type.replace(/_/g, ' ')}</h2>
                                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">{selectedAlert.event_status} Incident #{selectedAlert.id}</p>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedAlert(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-900 transition-all">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll p-5 space-y-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 shadow-sm">
                                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1.5">Target Asset</p>
                                        <p className="text-[11px] font-black text-slate-900">{selectedAlert.camera_name || selectedAlert.asset_hostname || "Unknown Camera"}</p>
                                        <p className="text-[10px] text-slate-500 font-bold font-mono mt-1">{selectedAlert.ip_address || "No IP Registered"}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 shadow-sm">
                                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1.5">Detection Time</p>
                                        <p className="text-[11px] font-black text-slate-900">{new Date(selectedAlert.updated).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</p>
                                        <p className="text-[10px] text-slate-500 font-bold mt-1 inline-flex items-center gap-1.5 uppercase tracking-widest">
                                            <span className={`w-1.5 h-1.5 rounded-full ${selectedAlert.event_status === 'closed' ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'}`} />
                                            {selectedAlert.event_status === 'closed' ? 'Historical' : 'Active Duty'}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 w-1 bg-orange-500 rounded-full" />
                                        <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Diagnostic Intelligence</h3>
                                    </div>
                                    <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                                        <p className="text-[11px] text-slate-700 font-bold leading-relaxed mb-3">{selectedAlert.message}</p>
                                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100/50">
                                            <p className="text-[10px] text-slate-500 font-bold leading-relaxed italic">{selectedAlert.description || "No granular diagnostic data provided by event engine."}</p>
                                        </div>
                                    </div>
                                </div>

                                {selectedAlert.event_data && (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <div className="h-4 w-1 bg-indigo-500 rounded-full" />
                                            <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Event Telemetry</h3>
                                        </div>
                                        <div className="space-y-2.5">
                                            {(() => {
                                                try {
                                                    const payload = JSON.parse(selectedAlert.event_data!);
                                                    const items = Array.isArray(payload) ? payload : [payload];
                                                    return items.map((snap, i) => (
                                                        <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-4 shadow-sm hover:border-slate-200 transition-all">
                                                            <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200/50">
                                                                <span className="text-[10px] font-black font-mono text-indigo-600 uppercase tracking-widest">STATE_ID: {snap.status || 'SYNC'}</span>
                                                                <span className="text-[9px] text-slate-400 font-bold">{new Date(snap.created || selectedAlert.updated).toLocaleTimeString()}</span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                                                                {Object.entries(snap.data || snap).map(([key, val]: [string, any]) => (
                                                                    <div key={key} className="flex flex-col min-w-0">
                                                                        <span className="text-[8px] text-slate-400 uppercase font-black tracking-widest mb-0.5">{key.replace(/_/g, ' ')}</span>
                                                                        <span className="text-[10px] text-slate-700 font-black font-mono truncate">
                                                                            {key.includes('time') || key.includes('at')
                                                                                ? new Date(val).toLocaleString([], { hour: '2-digit', minute: '2-digit' })
                                                                                : String(val)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ));
                                                } catch (e) {
                                                    return <pre className="text-[10px] bg-slate-900 text-slate-300 p-4 rounded-xl overflow-x-auto font-mono leading-relaxed">{selectedAlert.event_data}</pre>;
                                                }
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-5 border-t border-slate-100 bg-slate-50/50 mt-auto">
                                <button onClick={() => setSelectedAlert(null)} className="w-full py-3 bg-white hover:bg-slate-900 border border-slate-200 hover:border-slate-900 text-slate-900 hover:text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-xl shadow-sm transition-all active:scale-[0.98]">
                                    Dismiss Analysis
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
                <SidePanel isOpen={panelOpen === 'nvr-issues'} onClose={() => setPanelOpen(null)} icon={Database} title="NVR Health & Storage Audit" subtitle="Detailed breakdown of NVR persistence and storage anomalies" width="w-[500px]">
                    <div className="h-full flex flex-col overflow-hidden p-4 space-y-4">
                        {/* Critical Issues Section */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 px-1">
                                <div className="w-1.5 h-4 bg-rose-500 rounded-full" />
                                <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Critical Anomalies ({nvrData?.health?.critical_issues?.length || 0})</h3>
                            </div>
                            <div className="space-y-2 overflow-y-auto max-h-[300px] custom-scroll pr-2">
                                {nvrData?.health?.critical_issues?.map((issue: any, index: number) => (
                                    <div key={index} className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-3 group hover:border-rose-200 transition-all">
                                        <div className="mt-0.5 p-1.5 bg-rose-100 text-rose-600 rounded-lg group-hover:scale-110 transition-transform">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <p className="text-[9px] font-black text-rose-700 uppercase tracking-tighter">{issue.nvr}</p>
                                                <span className="text-[7px] font-black text-rose-400 uppercase">High Priority</span>
                                            </div>
                                            <p className="text-[10px] font-bold text-slate-700 leading-relaxed">{issue.issue}</p>
                                        </div>
                                    </div>
                                ))}
                                {(!nvrData?.health?.critical_issues || nvrData?.health?.critical_issues.length === 0) && (
                                    <div className="py-8 flex flex-col items-center justify-center border-2 border-dashed border-slate-100 rounded-2xl opacity-40">
                                        <div className="p-3 bg-slate-50 rounded-full mb-2"><Activity className="w-6 h-6 text-slate-300" /></div>
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">No Critical Issues</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Warnings Section */}
                        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
                            <div className="flex items-center gap-2 px-1 shrink-0">
                                <div className="w-1.5 h-4 bg-amber-500 rounded-full" />
                                <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">System Warnings ({nvrData?.health?.warnings?.length || 0})</h3>
                            </div>
                            <div className="space-y-2 overflow-y-auto flex-1 custom-scroll pr-2">
                                {nvrData?.health?.warnings?.map((item: any, index: number) => (
                                    <div key={index} className="p-3 bg-amber-50/50 border border-amber-100 rounded-xl flex items-start gap-3 group hover:border-amber-200 transition-all">
                                        <div className="mt-0.5 p-1.5 bg-amber-100 text-amber-600 rounded-lg group-hover:scale-110 transition-transform">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <p className="text-[9px] font-black text-amber-700 uppercase tracking-tighter">{item.nvr}</p>
                                                <span className="text-[7px] font-black text-amber-400 uppercase">Warning</span>
                                            </div>
                                            <p className="text-[10px] font-bold text-slate-700 leading-relaxed">{item.warning}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </SidePanel>

            </AnimatePresence>
        </div>
    );
};

export default CameraDashboard;
